import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Image } from 'react-bootstrap';
import moment from 'moment';

import ButtonLoader from './ButtonLoader';
import { loaderStatus } from '../store/features/loader';
import { getUserProfile } from '../store/features/auth';
import { NUMBERS } from '../resources/number-constants';
import { ROUTES } from '../resources/routes-constants';
import { EXTERNAL_URLS } from '../resources/url-constants';
import { getValue, renderCustomCloseButton, storeValue } from '../utility/helpers';
import { shouldShowUpgradeButton, handleUpgrade as handleUpgradeUtil } from '../utility/tierUtils';
import ThemeChange from '../components/common/ThemeChange';
import DropdownMenu from '../components/common/DropdownMenu';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import Description from '../components/employer/JobDescription/Description';
import { approvedJob, generateCandidate, getJobDetail, resetCandidates } from '../store/features/dashboard';
import { aiChat, chatBottonIcon, doneTick, likeWhite, logo } from '../assets/images/images';
import { toast } from 'react-toastify';
import { usePageTitle } from '../hooks/usePageTitle';

const JobDescriptionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const isLoading = useAppSelector(loaderStatus);
  const queryParams = new URLSearchParams(location.search);
  const jobId = queryParams.get('jobId') || '9';
  const jobAction = queryParams.get('action') || '';
  const aboutFor = queryParams.get('about') || '';
  const endpoint = location.pathname.substring(1);
  const checkGenerate = getValue('isGenerate');

  const [loading, setLoading] = useState(false);
  const [jobData, setJobData] = useState<any>({});
  const [showLoader, setShowLoader] = useState(false);
  const [generatedDate, setGeneratedDate] = useState('');
  const [dataGenerated, setDataGenerated] = useState(true);
  // Try to get cached tier data for faster initial render
  const cachedTierData = getValue('tierData') ? JSON.parse(getValue('tierData') || '{}') : {};
  const [assignedTier, setAssignedTier] = useState<any>(cachedTierData);
  const [hideChatWindow, setHideChatWindow] = useState(true);
  const [idealizeJobData, setIdealizeJobData] = useState<any>([]);
  const [percentageFitValue, setPercentageFitValue] = useState<any>(null);
  const [havesData, setHavesData] = useState<any>([]);
  const [candidateLoading, setCandidateLoading] = useState(false);

  // Define routes and headings in a map to reduce repetitive conditionals
  const routesMap: Record<string, string> = {
    'job-description': '/idealize-candidate',
    'idealize-candidate': '/job-description-summary',
    'job-description-summary': '/candidate-selection',
    'candidate-selection': '/qualified-candidates',
  };

  // 'job-description': jobAction !== 'edit' ? 'Job Search Review' : 'Job description Prioritization'
  const headingMap: Record<string, string> = {
    'job-description': 'Job Description',
    'idealize-candidate': 'Idealized Candidate Prioritization',
    'job-description-summary': aboutFor === 'saved' ? 'Saved job' : aboutFor === 'duplicateSaved' ? 'duplicate job search' : 'Review Summary',
    'candidate-selection': 'Candidate Selection',
  };

  const approveRoute = routesMap[endpoint] || '/candidate-selection';
  const pageHeading = headingMap[endpoint] || 'Candidate Selection';

  usePageTitle(pageHeading);

  const nextStep = endpoint === 'job-description'
    ? NUMBERS.TWO
    : endpoint === 'idealize-candidate'
      ? NUMBERS.THREE
      : endpoint === 'job-description-summary'
        ? NUMBERS.FOUR
        : NUMBERS.FIVE;

  const navigateTo = (route: string) => navigate(route);

  const handleUpgrade = () => {
    handleUpgradeUtil(navigate, ROUTES.CONTACT_US_TO_UPGRADE_ROUTE);
  };

  // Render button text dynamically
  const getButtonText = () => {
    if (showLoader) {
      return <ButtonLoader />;
    }
    if (jobAction === 'edit') {
      return 'Done';
    }

    switch (endpoint) {
      case 'job-description':
        return 'Approve & Next';
      case 'idealize-candidate':
        return 'Approve';
      case 'job-description-summary':
        return aboutFor === 'saved' ? 'Generate Candidates' : 'Confirm Job';
      case 'candidate-selection':
        return 'See Candidates';
      default:
        return '';
    }
  };

  // Consolidate the approve logic
  const handleApprove = async () => {
    const buttonText = getButtonText();
    if (buttonText === 'Generate Candidates' || buttonText === 'Confirm Job') {
      if (endpoint === 'job-description'
        || endpoint === 'job-description-summary') {
        for (const list of idealizeJobData) {
          if (list.title === "Won't-Haves") continue;
          if (!list.items || list.items.length === 0) {
            toast.error(`'${list.title}' should have at least 1 input.`, { closeButton: renderCustomCloseButton() });
            return false;
          }
        }
      } else {
        for (const list of havesData) {
          if (list.title === "Won't-Haves") continue;
          if (!list.items || list.items.length === 0) {
            toast.error(`'${list.title}' should have at least 1 input.`, { closeButton: renderCustomCloseButton() });
            return false;
          }
        }
      }
    }

    if (jobAction === 'edit') {
      if (endpoint === 'idealize-candidate') {
        for (const list of havesData) {
          if (list.title === "Won't-Haves") continue;
          if (!list.items || list.items.length === 0) {
            toast.error(`'${list.title}' should have at least 1 input.`, { closeButton: renderCustomCloseButton() });
            return false;
          }
        }
      }
      if (aboutFor) {
        navigateTo(`/job-description-summary?jobId=${jobId}&about=${aboutFor}`);
      } else {
        navigateTo(`/job-description-summary?jobId=${jobId}`);
      }
    } else {
      if (endpoint === 'candidate-selection') {
        // Read filter state saved by the CandidateSelection screen and transform
        // it into the API contract shape before sending to generate-candidate.
        let storedFilters: any = null;
        try {
          const raw = localStorage.getItem(`job:${jobId}:advancedFilters`);
          if (raw) storedFilters = JSON.parse(raw);
        } catch (e) {
          // ignore storage/parse errors
        }

        const payload: Record<string, any> = {
          job: jobId,
          percentage_fit: percentageFitValue,
          distance_in_miles: storedFilters?.distance_in_miles ?? null,
        };

        if (storedFilters) {
          // --- Multi-location ---
          const storedLocations: string[] = Array.isArray(storedFilters.locations)
            ? storedFilters.locations
            : (storedFilters.locations ? [storedFilters.locations] : []);
          if (storedLocations.length > 1) {
            payload.cities = storedLocations;
          } else if (storedLocations.length === 1) {
            payload.city = storedLocations[0];
          }

          // --- filters object ---
          const filtersObj: Record<string, any> = {};

          // Total experience range
          const expMin = typeof storedFilters.experience_min === 'number' ? storedFilters.experience_min : 0;
          const expMax =
            typeof storedFilters.experience_max === 'number'
              ? storedFilters.experience_max
              : storedFilters.experience_score ??
                storedFilters.total_years_experience ??
                storedFilters.years_experience ??
                0;
          if (expMin > 0 || expMax > 0) {
            filtersObj.total_experience_min_years = expMin;
            filtersObj.total_experience_max_years = expMax;
          }

          // Boolean toggles (always explicit)
          filtersObj.has_publications = !!storedFilters.has_publications;
          filtersObj.has_volunteering = !!storedFilters.has_volunteering;
          filtersObj.has_test_scores = !!storedFilters.has_test_scores;
          filtersObj.has_projects = !!storedFilters.has_projects;

          // Job title filters (structured)
          const storedJobTitleFilters: any[] = Array.isArray(storedFilters.job_title_filters)
            ? storedFilters.job_title_filters
            : [];
          const parseTitleYears = (v: any): number | null => {
            if (v === null || v === undefined || v === '') return null;
            const n = Number(v);
            if (!Number.isFinite(n)) return null;
            return Math.min(100, Math.max(0, Math.round(n)));
          };
          const structuredTitleFilters = storedJobTitleFilters
            .filter((f: any) => f?.title)
            .map((f: any) => {
              const legacy = parseTitleYears(f?.years_in_role) ?? 0;
              const minP = parseTitleYears(f?.years_in_role_min);
              const maxP = parseTitleYears(f?.years_in_role_max);
              let lo = minP ?? 0;
              let hi = maxP ?? legacy;
              if (hi === 0 && legacy > 0) hi = legacy;
              if (lo > hi) {
                const t = lo;
                lo = hi;
                hi = t;
              }
              const out: Record<string, any> = {
                name: String(f.title),
                current_or_last_12_months: !!f.current_or_last_12_months,
              };
              if (hi > 0) {
                out.years_max_years = hi;
                if (lo > 0) out.years_min_years = lo;
                out.years = hi;
              }
              return out;
            });
          if (structuredTitleFilters.length > 0) {
            filtersObj.job_title_filters = structuredTitleFilters;
          }

          // Role experience filters (AND match — titles that also carry years_in_role)
          const roleExpFilters = storedJobTitleFilters
            .filter((f: any) => {
              if (!f?.title) return false;
              const legacy = parseTitleYears(f?.years_in_role) ?? 0;
              const maxP = parseTitleYears(f?.years_in_role_max);
              let hi = maxP ?? legacy;
              if (hi === 0 && legacy > 0) hi = legacy;
              return hi > 0;
            })
            .map((f: any) => {
              const legacy = parseTitleYears(f?.years_in_role) ?? 0;
              const minP = parseTitleYears(f?.years_in_role_min);
              const maxP = parseTitleYears(f?.years_in_role_max);
              let lo = minP ?? 0;
              let hi = maxP ?? legacy;
              if (hi === 0 && legacy > 0) hi = legacy;
              if (lo > hi) {
                const t = lo;
                lo = hi;
                hi = t;
              }
              const row: Record<string, any> = { name: String(f.title), years: hi, years_max_years: hi };
              if (lo > 0) row.years_min_years = lo;
              return row;
            });
          if (roleExpFilters.length > 0) {
            filtersObj.role_experience_filters = roleExpFilters;
          }

          // Industry experience filters (AND match)
          const storedIndustryFilters: any[] = Array.isArray(storedFilters.industry_experience_filters)
            ? storedFilters.industry_experience_filters
            : [];
          const industryExpFilters = storedIndustryFilters
            .filter((f: any) => f?.industry)
            .map((f: any) => {
              const legacy = parseTitleYears(f?.years) ?? 0;
              const minP = parseTitleYears(f?.years_min);
              const maxP = parseTitleYears(f?.years_max);
              let lo = minP ?? 0;
              let hi = maxP ?? legacy;
              if (hi === 0 && legacy > 0) hi = legacy;
              if (lo > hi) {
                const t = lo;
                lo = hi;
                hi = t;
              }
              const row: Record<string, any> = {
                name: String(f.industry),
                ...(f?.current_or_last_12_months ? { current_or_last_12_months: true } : {}),
              };
              if (hi > 0) {
                row.years_max_years = hi;
                if (lo > 0) row.years_min_years = lo;
                row.years = hi;
              }
              return row;
            });
          if (industryExpFilters.length > 0) {
            filtersObj.industry_experience_filters = industryExpFilters;
          }

          // Companies worked in (structured)
          const storedCompanies: any[] = Array.isArray(storedFilters.companies_worked_in_filters)
            ? storedFilters.companies_worked_in_filters
            : (Array.isArray(storedFilters.companies_worked_in)
              ? storedFilters.companies_worked_in.map((name: any) => ({ name, current_or_last_12_months: true }))
              : []);
          const companyFilters = storedCompanies
            .filter((c: any) => c?.name || c?.company)
            .map((c: any) => ({
              name: String(c?.name || c?.company).trim(),
              ...(c?.current_or_last_12_months ? { current_or_last_12_months: true } : {}),
            }));
          if (companyFilters.length > 0) {
            filtersObj.companies_worked_in = companyFilters;
          }

          if (Object.keys(filtersObj).length > 0) {
            payload.filters = filtersObj;
          }
        }
        setShowLoader(true);
        try {
          await dispatch(generateCandidate(payload)).unwrap();
          dispatch(resetCandidates());
          if (aboutFor) {
            navigateTo(`/qualified-candidates?jobId=${jobId}&fitPercent=${percentageFitValue}&about=${aboutFor}`);
          } else {
            navigateTo(`/qualified-candidates?jobId=${jobId}&fitPercent=${percentageFitValue}`);
          }
        } catch (error) {
          console.info('Error:', error);
        } finally {
          setShowLoader(false);
        }
      } else {
        if (endpoint === 'idealize-candidate') {
          for (const list of havesData) {
            if (list.title === "Won't-Haves") continue;
            if (!list.items || list.items.length === 0) {
              toast.error(`'${list.title}' should have at least 1 input.`, { closeButton: renderCustomCloseButton() });
              return false;
            }
          }
        }
        const payload = {
          approve_next: true,
          upcoming_step: nextStep,
        };
        setShowLoader(true);
        try {
          await dispatch(approvedJob({ payload, id: jobId })).unwrap();
          if (aboutFor) {
            navigateTo(`${approveRoute}?jobId=${jobId}&about=${aboutFor}`);
          } else {
            navigateTo(`${approveRoute}?jobId=${jobId}`);
          }
        } catch (error) {
          console.info('Error:', error);
        } finally {
          setShowLoader(false);
        }
      }
    }
  };

  const handleClose = async () => {
    if (jobAction === 'edit') {
      if (aboutFor) {
        navigateTo(`/job-description-summary?jobId=${jobId}&about=${aboutFor}`);
      } else {
        navigateTo(`/job-description-summary?jobId=${jobId}`);
      }
    } else {
      const payload = { approve_next: false, upcoming_step: nextStep };
      try {
        await dispatch(approvedJob({ payload, id: jobId })).unwrap();
        navigateTo('/dashboard');
      } catch (error) {
        console.info('Error:', error);
      }
    }
  };



  const transformJobData = (jobDetail: any) => {
    return [
      {
        title: 'Must-Haves',
        items: jobDetail?.must_have?.title || [],
        limit: 5,
        showLimit: true
      },
      {
        title: 'Should-Haves',
        items: jobDetail?.should_have?.title || [],
        limit: 7,
        showLimit: true
      },
      {
        title: 'Good-to-Haves',
        items: jobDetail?.good_to_have?.title || [],
        limit: 10,
        showLimit: true
      },
      {
        title: "Won't-Haves",
        items: jobDetail?.wont_have?.title || [],
        limit: 10,
        showLimit: true
      },
      {
        title: 'Could-Haves',
        items: jobDetail?.could_have?.title || [],
        limit: 150,
        showLimit: false
      },
    ];
  };

  const fetchJobDetails = useCallback(async (id: any, reloadRequired: any) => {
    try {
      setLoading(reloadRequired);
      const res = await dispatch(getJobDetail(id)).unwrap();
      setJobData(res?.data);
      setGeneratedDate(res?.data?.updated_at);
      setIdealizeJobData(transformJobData(res?.data));
      setLoading(false);
      localStorage.removeItem('isGenerate');
    } catch (error) {
      console.info('Error during fetching user details:', error);
      localStorage.removeItem('isGenerate');
    }
  }, [dispatch, setGeneratedDate]);

  const refreshJobData = (isRefresh: any = true) => {
    fetchJobDetails(jobId, isRefresh);
  };

  const fetchUserDetails = useCallback(async () => {
    try {
      const companyId = getValue('companyId');
      const payload = {
        companyId,
        loader: false
      };
      const res = await dispatch(getUserProfile(payload)).unwrap();
      storeValue('canCreateJob', res.data.job_left);
      if (res?.data?.tier_assigned) {
        // Cache tier data for faster loading on subsequent visits
        storeValue('tierData', JSON.stringify(res?.data?.tier_assigned));
        setAssignedTier(res?.data?.tier_assigned);
      }
    } catch (error) {
      console.info('Error during fetching user details:', error);
    }
  }, [dispatch]);

  useEffect(() => {
    if (checkGenerate) {
      setDataGenerated(false);
    } else {
      setDataGenerated(true);
    }
  }, [checkGenerate]);

  useEffect(() => {
    // Only fetch user details once on mount
    fetchUserDetails();
    // fetchUserDetails is stable (only depends on dispatch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (jobId) {
      fetchJobDetails(jobId, true);
    }
  }, [jobId, fetchJobDetails, endpoint]);

  // CandidateSelection syncs loadGraph -> candidateLoading; leaving that step unmounts the child
  // without resetting parent state, so "Generate Candidates" on the saved summary could stay disabled.
  useEffect(() => {
    if (endpoint !== 'candidate-selection') {
      setCandidateLoading(false);
    }
  }, [endpoint]);

  return (
    <>
      <div className={`sign-in-header dashboard-header chat-header job-des-header ${!dataGenerated ? 'd-none' : ''}`}>
        <div className="container-fluid">
          <div className="chat-header-inner">
            <img
              src={logo}
              alt="logo"
              onClick={() => navigateTo('/dashboard')}
              role="button"
              aria-label="Go to dashboard"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigateTo('/dashboard'); } }}
              className="brand-logo"
            />
            <div className="welcome-wrap">
              <div className='dash-job-head'>
                <h4 className="dashboard-subhead text-uppercase">{pageHeading}</h4>
                {(((aboutFor === 'saved') || (aboutFor === 'duplicateSaved')) && (endpoint === 'job-description-summary')) && (
                  <p className='sub-desc-date'>
                    {generatedDate ? moment(generatedDate).format('MM/DD/YYYY') : ''}
                  </p>
                )}
              </div>
            </div>
            <div className={`chat-header-right ${showLoader ? 'p-none' : ''}`}>
              <Button
                variant="primary"
                className={`upgrade-header-btn ${!assignedTier || !shouldShowUpgradeButton(assignedTier) ? 'hidden' : ''}`}
                onClick={handleUpgrade}
              // Using CSS class for styling
              >
                Contact us to upgrade
              </Button>
              <Button
                disabled={(endpoint === 'candidate-selection' && !percentageFitValue) || isLoading || candidateLoading}
                variant="dark"
                onClick={handleApprove}
              >
                {endpoint !== 'candidate-selection' && (
                  <Image
                    src={likeWhite}
                    height={23}
                    width={23}
                    alt="arrow"
                    className="prefix-icon"
                  />
                )}
                <span className="text">{getButtonText()}</span>
              </Button>
              <Button className="btn-cancel" onClick={handleClose}>
                {jobAction === 'edit' ? 'Cancel' : 'Close'}
              </Button>
              <DropdownMenu assignedTier={assignedTier} />
            </div>
          </div>
        </div>
      </div>

      <section
        className={`preview-container ${!dataGenerated ? 'd-none' : ''} ${endpoint === 'job-description-summary' ? 'preview-detail-container' : 'preview-container-chat'}`}>
        <ThemeChange />
        <Description
          jobId={jobId}
          step={endpoint}
          loading={loading}
          jobData={jobData}
          jobAction={jobAction}
          aboutFor={aboutFor}
          setShowLoader={setShowLoader}
          idealizeJobData={idealizeJobData}
          setPercentageFitValue={setPercentageFitValue}
          hideChatWindow={hideChatWindow}
          setHideChatWindow={setHideChatWindow}
          refreshJobData={refreshJobData}
          setHavesData={setHavesData}
          handleClose={handleClose}
          setCandidateLoading={setCandidateLoading}
          assignedTier={assignedTier}
        />

        <div className="mobile-footer">
          {((endpoint === 'job-description') || (endpoint === 'idealize-candidate')) && (
            <Image
              src={aiChat}
              height={60}
              width={60}
              alt="Chat bot"
              className="btn-round"
              onClick={() => setHideChatWindow(false)}
              role="button"
              aria-label="Open chat"
              tabIndex={0}
              onKeyDown={(e: any) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setHideChatWindow(false); } }}
            />
          )}
          <Button
            variant="dark"
            className='generate-candidate-btn'
            disabled={(endpoint === 'candidate-selection' && !percentageFitValue) || isLoading || candidateLoading}
            onClick={handleApprove}
          >
            {endpoint !== 'candidate-selection' && (
              <Image
                src={likeWhite}
                height={23}
                width={23}
                alt="arrow"
                className="prefix-icon"
              />
            )}
            <span className="text">{getButtonText()}</span>
          </Button>
        </div>
      </section>

      <section className={`chat-section chat-loading-page chat-dashboard-loading-sec ${!dataGenerated ? '' : 'chat-section-done'}`}>
        <div className="video-container">
          <video
            className="footer-video"
            controlsList="nodownload"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
          >
            <source src={EXTERNAL_URLS.VIDEOS.LANDING_PAGE} type="video/mp4" />
          </video>
          <div className="video-overlay"></div>
        </div>
        <div className="container">
          <div className="chat-dashboard-loading done-status">
            <div className="done-wrap">
              <p className="chat-dashboard-loading-des">Done</p>
              <span className="green-tick"><img src={doneTick} alt="tick" /></span>
            </div>
          </div>
        </div>
        <img src={chatBottonIcon} alt="brand" className="chat-bottom-img" />
      </section>
    </>
  );
};

export default JobDescriptionPage;
